<template>
  <div v-if="seal" class="flex flex-col gap-1" :data-testid="testId">
    <span
      class="inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-bold"
      :class="QUEUE_TONE_PILL_CLASS[seal.tone]"
      :title="compact ? compactTitle : undefined"
      :data-testid="`${testId}-seal`"
    >
      {{ compact ? seal.shortLabel : seal.label }}
    </span>
    <ul v-if="!compact && reasonLines.length" class="m-0 list-none p-0 text-xs text-neutral50">
      <li v-for="line in reasonLines" :key="line" :data-testid="`${testId}-reason`">
        {{ line }}
      </li>
    </ul>
  </div>
  <span
    v-else-if="showUncalculated"
    class="text-xs text-neutral50"
    :data-testid="`${testId}-uncalculated`"
  >
    Elegibilidade ainda não calculada
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { QUEUE_TONE_PILL_CLASS } from '@/components/designSystem/QueueViews/types'
import {
  formatEligibilityReasons,
  resolveEligibilitySeal
} from '@/constants/payment-eligibility.constants'
import type { PaymentEligibilityView } from '@/types/payment-eligibility.types'

/**
 * Selo de elegibilidade de UM plantão (spec antecipe-registration §6, Q16/Q25):
 * "Elegível Antecipe / À vista / Só Fluxo" + motivos. `eligibility: null` é "não
 * calculado" — nunca "Só Fluxo".
 */
const props = withDefaults(
  defineProps<{
    eligibility: PaymentEligibilityView | null
    /** Célula estreita: rótulo curto e motivos no `title`. */
    compact?: boolean
    /** Mostra "Elegibilidade ainda não calculada" em vez de sumir. */
    showUncalculated?: boolean
    testId?: string
  }>(),
  { compact: false, showUncalculated: false, testId: 'payment-eligibility' }
)

const seal = computed(() => (props.eligibility ? resolveEligibilitySeal(props.eligibility) : null))

const reasonLines = computed(() =>
  props.eligibility ? formatEligibilityReasons(props.eligibility.reasons) : []
)

const compactTitle = computed(() =>
  seal.value
    ? [seal.value.label, ...reasonLines.value].join(reasonLines.value.length ? ' — ' : '')
    : undefined
)
</script>
